import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { Archive, Copy, Paperclip, Tag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useHotkeys } from 'react-hotkeys-hook'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { normalizeToMinute } from '../utils/datetime'
import { resolveTaskIdentifier } from '../utils/taskIdentifier'
import { useTaskReminders } from '../hooks/useTaskReminders'
import { useProjects } from '../hooks/useProjects'
import { TaskDescriptionEditor } from './TaskDescriptionEditor'
import { ChecklistSection } from './ChecklistSection'
import { SubTaskSection } from './SubTaskSection'
import { RelationsSection } from './RelationsSection'
import { ParentTaskIndicator } from './ParentTaskIndicator'
import { TaskOptionsMenu } from './TaskOptionsMenu'
import { TaskMetadataSection } from './TaskMetadataSection'
import { StatusComboBox } from './StatusComboBox'
import { AiGateField } from './AiGateField'
import { LinkedPullRequestsSection } from './LinkedPullRequestsSection'
import { TaskDetailsHeader } from './TaskDetailsHeader'
import { TaskDiscussionStrip } from './TaskDiscussionStrip'
import { TaskProgressSummary } from './TaskProgressSummary'
import { TaskCycleField } from './TaskCycleField'
import type { GridDensity } from './TaskMetadataSection'
import { useCreateSubTask } from '../hooks/useCreateSubTask'
import type { DiscussionExtraTab } from './TaskDiscussionStrip'
import type { RecurrencePattern, RecurrenceType  } from '@/lib/types/recurrence'
import type { EstimationType } from '@/lib/types/estimation'
import type { TaskDetailsProps } from '../type'
import { PICKER_SIDE_OFFSET, pickerPopoverClassName } from '@/components/app/combo-box/PickerPanel'
import { useEntityLinkSuggestions } from '@/components/editor/useEntityLinkSuggestions'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { BacklinksSection } from '@/components/features/backlinks/BacklinksSection'
import { ExplicitLinksSection } from '@/components/features/backlinks/ExplicitLinksSection'
import {
  useEntityLinks,
  useReminderMutations,
  useTaskLabelMutations,
  useTaskMutations,
  useTaskRelationMutations,
  useTaskRelations,
  useChecklists,
} from '@/lib/db/rxdb/hooks'
import { RELATION_TYPES } from '@/lib/types/taskRelation'
import { cn } from '@/lib/utils'
import useLoginStore from '@/store/LoginStore'
import { useProjectStatusMode } from '@/hooks/useProjectStatusMode'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { ReactionBar } from '@/components/features/reactions'
import { LabelList } from '@/components/features/labels/components'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useAllLabels } from '@/lib/hooks/useLabels'
import { useAllLabelGroups } from '@/lib/hooks/useLabelGroups'
import { useTaskLabels } from '@/lib/hooks/useTaskLabels'
import { AttachmentsSection } from '@/components/features/attachments'
import { AddCoverButton, CoverImage, CoverImagePicker, useCoverImage } from '@/components/features/cover-image'
import { LabelSelectionPanel } from '@/components/features/labels/components/LabelSelectionPanel'
import { getPlatformShortcutKeys } from '@/lib/utils/keyboard'
import { useAttachments } from '@/lib/hooks/useAttachments'
import { FILE_INPUT_ACCEPT } from '@/components/features/attachments/config'
import { TaskStatusCheckbox } from '@/components/app/tasks/TaskStatusCheckbox'
import { Badge } from '@/components/ui/badge'
import { logger } from '@/lib/monitoring/logger'
import { getRxDB } from '@/lib/db/rxdb'
import { useCustomFieldsForTask } from '@/components/features/custom-fields/useCustomFieldsForTask'
import { CustomFieldTabContent } from '@/components/features/custom-fields/CustomFieldTabContent'
import { CustomFieldInline } from '@/components/features/custom-fields/CustomFieldInline'
import { StarToggle } from '@/components/app/StarToggle'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { useMembers } from '@/lib/hooks/useWorkspaceUsers'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { useElementWidth } from '@/hooks/useElementWidth'
import { useIsMobile } from '@/hooks/useMobile'


// removed – now using variant="underline" on TabsList / TabsTrigger

const UNTITLED_TASK_NAME = 'Untitled task'
const DEFAULT_DOCUMENT_TITLE = 'Moly Platform'
const MAX_DOCUMENT_TITLE_LENGTH = 80

const toTimestamp = (value: Date | string | number | null | undefined) => {
  if (!value) {
    return 0
  }

  if (typeof value === 'number') {
    return value
  }

  if (value instanceof Date) {
    return value.getTime()
  }

  const parsed = new Date(value)
  const timestamp = parsed.getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

/**
 * Helper to handle optimistic update protection with server confirmation
 * @returns object indicating if should update local state and clear ref
 */
function shouldAcceptServerValue<T>(
  serverValue: T,
  localEdit: { timestamp: number; value: T } | null,
  serverUpdatedOn: number,
): { shouldAccept: boolean; shouldClearRef: boolean } {
  if (!localEdit) {
    // No pending edit, accept server value
    return { shouldAccept: true, shouldClearRef: false }
  }

  const valuesMatch = serverValue === localEdit.value
  const timeDiff = serverUpdatedOn - localEdit.timestamp

  if (valuesMatch && timeDiff > 1000) {
    // Server has confirmed our change (value matches AND timestamp is >1s newer)
    return { shouldAccept: true, shouldClearRef: true }
  }

  if (!valuesMatch && timeDiff > 3000) {
    // Server update is more than 3 seconds newer with different value
    return { shouldAccept: true, shouldClearRef: true }
  }

  // Keep local optimistic value
  return { shouldAccept: false, shouldClearRef: false }
}

function TaskIdentifierPill({ identifier }: { identifier: string }) {
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(identifier)
        toast.success(`Copied ${identifier}`)
      }}
      title="Copy reference"
      className="group mb-1 inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-1.5 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {identifier}
      <Copy className="h-3 w-3 opacity-50 transition-opacity group-hover:opacity-100" aria-hidden="true" />
    </button>
  )
}

export function TaskDetails({
  task,
  statuses,
  parentTask = null,
  className,
  layout = 'horizontal',
  onSubTaskClick,
  onParentTaskClick,
  chrome,
}: TaskDetailsProps) {
  const [nameValue, setNameValue] = useState('')
  const [dueDateValue, setDueDateValue] = useState<Date | undefined>()
  const [startDateValue, setStartDateValue] = useState<Date | undefined>()
  const [statusIdValue, setStatusIdValue] = useState<string | null>(null)
  const [priorityValue, setPriorityValue] = useState<number>(0)
  const [userIdValue, setUserIdValue] = useState<string | null>(null)
  const [accountableUserIdValue, setAccountableUserIdValue] = useState<string | null>(null)
  const [isSomeday, setIsSomeday] = useState(false)
  const [reminderOffsetMinutes, setReminderOffsetMinutes] = useState<
    number | null
  >(null)
  const [recurrencePattern, setRecurrencePattern] =
    useState<RecurrencePattern | null>(null)
  const [estimationValue, setEstimationValue] = useState<number | null>(null)
  const [workItemTypeIdValue, setWorkItemTypeIdValue] = useState<string | null>(null)
  const [isPriorityPopoverOpen, setIsPriorityPopoverOpen] = useState(false)
  const [isAssigneePopoverOpen, setIsAssigneePopoverOpen] = useState(false)
  const [isProjectPopoverOpen, setIsProjectPopoverOpen] = useState(false)
  const lastNameEditRef = useRef<{ timestamp: number; value: string } | null>(
    null,
  )
  const lastStatusEditRef = useRef<{
    timestamp: number
    value: string | null
  } | null>(null)
  const lastPriorityEditRef = useRef<{
    timestamp: number
    value: number
  } | null>(null)
  const lastUserIdEditRef = useRef<{
    timestamp: number
    value: string | null
  } | null>(null)
  const lastAccountableUserIdEditRef = useRef<{
    timestamp: number
    value: string | null
  } | null>(null)
  const lastSomedayEditRef = useRef<{
    timestamp: number
    value: boolean
  } | null>(null)
  const lastEstimationEditRef = useRef<{
    timestamp: number
    value: number | null
  } | null>(null)
  const lastStartDateEditRef = useRef<{
    timestamp: number
    value: number | null
  } | null>(null)
  const lastDueDateEditRef = useRef<{
    timestamp: number
    value: number | null
  } | null>(null)
  const baseDocumentTitleRef = useRef(DEFAULT_DOCUMENT_TITLE)
  const taskDetailsRef = useRef<HTMLDivElement>(null)
  const assigneeShortcutKeys = useMemo(
    () => getPlatformShortcutKeys('U'),
    [],
  )
  const projectShortcutKeys = useMemo(() => ['Shift', 'P'], [])
  const labelShortcutKeys = useMemo(
    () => getPlatformShortcutKeys('L'),
    [],
  )

  // Get current user's workspace
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const workspaceSlug = useLoginStore((state) => state.workspaceSlug)
  const userId = useLoginStore((state) => state.userId)
  const navigate = useNavigate()

  // Populate entity link suggestions for [[ trigger in editor
  useEntityLinkSuggestions()

  // Check if project uses advanced status management
  const { isPersonal } = useWorkspaceMode()
  const { statusMode } = useProjectStatusMode(task.projectId)

  // RxDB mutation hooks
  const { updateTask: rawUpdateTask, archiveTask, unarchiveTask } = useTaskMutations()
  const { deleteTaskRelation } = useTaskRelationMutations()
  const { createReminder, updateReminder, deleteReminder } =
    useReminderMutations()
  const { createTaskLabel, deleteTaskLabel } =
    useTaskLabelMutations()

  // Relations & Links counts for tab badges
  const { relations } = useTaskRelations(task?.id)
  const { links: entityLinks } = useEntityLinks(task?.id)
  const parentChildRelation = useMemo(
    () =>
      parentTask
        ? relations.find(
            (relation) =>
              relation.relationType === RELATION_TYPES.PARENT_CHILD &&
              relation.sourceTaskId === parentTask.id &&
              relation.targetTaskId === task.id,
          )
        : undefined,
    [relations, parentTask, task.id],
  )

  // Batched updateTask — collects multiple field updates within the same tick
  // into a single RxDB write to avoid revision conflicts
  const pendingUpdatesRef = useRef<Record<string, { id: string; updates: Partial<any> }>>({})
  const flushScheduledRef = useRef<Record<string, boolean>>({})

  const updateTask = useCallback(
    async (id: string, updates: Partial<any>): Promise<void> => {
      // Merge into pending updates for this entity
      pendingUpdatesRef.current[id] = {
        id,
        updates: { ...pendingUpdatesRef.current[id]?.updates, ...updates },
      }

      // Schedule flush once per microtask per entity
      if (!flushScheduledRef.current[id]) {
        flushScheduledRef.current[id] = true
        await Promise.resolve() // wait one microtask
        const pending = pendingUpdatesRef.current[id]
        delete pendingUpdatesRef.current[id]
        flushScheduledRef.current[id] = false
        if (pending) {
          await rawUpdateTask(pending.id, pending.updates)
        }
      }
    },
    [rawUpdateTask],
  )

  const handleUnlinkParentTask = useCallback(async () => {
    await updateTask(task.id, { parentId: null })
    if (parentChildRelation) {
      await deleteTaskRelation(parentChildRelation.id)
    }
  }, [deleteTaskRelation, parentChildRelation, task.id, updateTask])

  // Fetch reminders for this task
  const { primaryReminder } = useTaskReminders(
    task.id,
    workspaceId,
  )

  // Fetch labels
  const { labels: availableLabels } = useAllLabels(workspaceId)
  const { labelGroups } = useAllLabelGroups(workspaceId)
  const { taskLabels, labels: selectedLabels } = useTaskLabels(
    task.id,
    workspaceId,
  )

  const selectedLabelIds = useMemo(
    () => taskLabels.map((taskLabel) => taskLabel.labelId),
    [taskLabels],
  )

  // Fetch attachments
  const { attachments, uploadFile, uploadProgress } = useAttachments({
    workspaceId: workspaceId ?? '',
    entityType: ENTITY_TYPES.WORK_ITEM,
    entityId: task.id,
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Show attachments section if there are attachments or upload in progress
  const shouldShowAttachmentsSection = attachments.length > 0 || uploadProgress.length > 0

  // Fetch projects
  const { projects } = useProjects()
  const taskProject = useMemo(() => {
    if (!task?.projectId) {
      return null
    }

    return projects.find((project) => project.id === task.projectId) ?? null
  }, [projects, task?.projectId])
  const taskIdentifier = useMemo(
    () => resolveTaskIdentifier(task, taskProject?.projectKey),
    [task, taskProject?.projectKey],
  )
  const estimationType: EstimationType = isPersonal
    ? 'none'
    : (taskProject?.estimationType as EstimationType) || 'none'
  const isWorkItemTypeEnabled =
    FEATURE_FLAGS.ENABLE_WORK_ITEM_TYPES && !isPersonal && (taskProject?.isWorkItemTypeEnabled ?? false)

  // Custom fields — bucketed into renderable tabs (renderMode === 'tab') + flat
  // inline pills (renderMode === 'inline'), with synthetic fallback tabs while real
  // tab docs are still streaming in via initial replication.
  const {
    tabs: customFieldTabs,
    inlineFields: customInlineFields,
    valueMap: customFieldValueMap,
  } = useCustomFieldsForTask(
    task.id,
    task.projectId ?? null,
    task.workItemTypeId ?? null,
    workspaceId ?? null,
  )
  const { t } = useTranslation('tasks')

  const [isLabelPickerOpen, setIsLabelPickerOpen] = useState(false)
  const [isCoverPickerOpen, setIsCoverPickerOpen] = useState(false)

  // Cover image
  const {
    displayUrl: coverDisplayUrl,
    meta: coverMeta,
    hasCover,
    isUploading: isCoverUploading,
    uploadProgress: coverUploadProgress,
    setCoverFromUnsplash,
    uploadCustomImage,
    removeCoverImage,
    updatePosition: updateCoverPosition,
  } = useCoverImage({
    entityId: task.id,
    workspaceId: workspaceId ?? '',
    coverImageUrl: task.coverImageUrl,
    coverImageType: task.coverImageType,
    coverImageMeta: task.coverImageMeta,
    updateEntity: updateTask,
    uploadCategory: 'task-cover',
  })

  useHotkeys(
    'p',
    (event) => {
      if (!taskDetailsRef.current) {
        return
      }

      const activeElement = document.activeElement
      // Allow when no active element, body is focused (clicked neutral area), or focus is within container
      const isWithinTaskDetails =
        !activeElement ||
        activeElement === document.body ||
        taskDetailsRef.current.contains(activeElement)

      if (!isWithinTaskDetails) {
        return
      }

      // Don't trigger when typing in text inputs
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement as HTMLElement)?.isContentEditable
      ) {
        return
      }

      event.preventDefault()
      setIsPriorityPopoverOpen(true)
    },
    { enableOnFormTags: true },
    [taskDetailsRef],
  )

  useHotkeys(
    'mod+u',
    (event) => {
      if (!taskDetailsRef.current) {
        return
      }

      const activeElement = document.activeElement
      const isWithinTaskDetails =
        !activeElement || taskDetailsRef.current.contains(activeElement)

      if (!isWithinTaskDetails) {
        return
      }

      event.preventDefault()
      setIsAssigneePopoverOpen(true)
    },
    { enableOnFormTags: true },
    [taskDetailsRef],
  )

  useHotkeys(
    'mod+l',
    (event) => {
      if (!taskDetailsRef.current) {
        return
      }

      const activeElement = document.activeElement
      const isWithinTaskDetails =
        !activeElement || taskDetailsRef.current.contains(activeElement)

      if (!isWithinTaskDetails) {
        return
      }

      event.preventDefault()
      setIsLabelPickerOpen(true)
    },
    { enableOnFormTags: true },
    [taskDetailsRef],
  )

  useHotkeys(
    'shift+p',
    (event) => {
      if (!taskDetailsRef.current) {
        return
      }

      const activeElement = document.activeElement
      // Allow when no active element, body is focused (clicked neutral area), or focus is within container
      const isWithinTaskDetails =
        !activeElement ||
        activeElement === document.body ||
        taskDetailsRef.current.contains(activeElement)

      if (!isWithinTaskDetails) {
        return
      }

      // Don't trigger when typing in text inputs
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement as HTMLElement)?.isContentEditable
      ) {
        return
      }

      event.preventDefault()
      setIsProjectPopoverOpen(true)
    },
    { enableOnFormTags: true },
    [taskDetailsRef],
  )

  // Sync reminder from query - use stable value to prevent loops
  useEffect(() => {
    const offsetValue = primaryReminder?.relativeOffsetMinutes ?? null
    setReminderOffsetMinutes((prev) => {
      // Only update if actually different to prevent cascading updates
      if (prev === offsetValue) return prev
      return offsetValue
    })
  }, [primaryReminder?.relativeOffsetMinutes])

  // Sync recurrence pattern from query
  useEffect(() => {
    if (primaryReminder && primaryReminder.recurrenceType !== null) {
      const pattern: RecurrencePattern = {
        recurrenceType: primaryReminder.recurrenceType as RecurrenceType,
        recurrenceInterval: primaryReminder.recurrenceInterval,
        recurrenceDaysOfWeek: primaryReminder.recurrenceDaysOfWeek,
        recurrenceDayOfMonth: primaryReminder.recurrenceDayOfMonth,
        recurrenceEndDate: primaryReminder.recurrenceEndDate
          ? (new Date(primaryReminder.recurrenceEndDate)
              .toISOString()
              .split('T')[0] ?? null)
          : null,
        recurrenceMaxOccurrences: primaryReminder.recurrenceMaxOccurrences,
      }
      setRecurrencePattern(pattern)
    } else {
      setRecurrencePattern(null)
    }
  }, [
    primaryReminder?.recurrenceType,
    primaryReminder?.recurrenceInterval,
    primaryReminder?.recurrenceDaysOfWeek,
    primaryReminder?.recurrenceDayOfMonth,
    primaryReminder?.recurrenceEndDate,
    primaryReminder?.recurrenceMaxOccurrences,
  ])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const initialTitle =
      document.title?.trim() || DEFAULT_DOCUMENT_TITLE
    baseDocumentTitleRef.current = initialTitle

    return () => {
      document.title = initialTitle
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const baseTitle =
      baseDocumentTitleRef.current || DEFAULT_DOCUMENT_TITLE

    if (!task) {
      document.title = baseTitle
      return
    }

    const identifierPart = taskIdentifier?.trim()
    const namePart = task.name?.trim()

    let composedTitle = ''
    if (identifierPart && namePart) {
      composedTitle = `${identifierPart} · ${namePart}`
    } else if (identifierPart) {
      composedTitle = identifierPart
    } else if (namePart) {
      composedTitle = namePart
    }

    if (!composedTitle) {
      document.title = baseTitle
      return
    }

    if (composedTitle.length > MAX_DOCUMENT_TITLE_LENGTH) {
      composedTitle = `${composedTitle.slice(
        0,
        MAX_DOCUMENT_TITLE_LENGTH - 1,
      )}…`
    }

    document.title = `${composedTitle} | ${baseTitle}`
  }, [task?.id, task?.name, taskIdentifier])

  useEffect(() => {
    if (!task) {
      setNameValue('')
      setDueDateValue(undefined)
      setStartDateValue(undefined)
      setStatusIdValue(null)
      setPriorityValue(0)
      setUserIdValue(null)
      setAccountableUserIdValue(null)
      setIsSomeday(false)
      setEstimationValue(null)
      setReminderOffsetMinutes(null)
      setWorkItemTypeIdValue(null)
      lastNameEditRef.current = null
      lastStatusEditRef.current = null
      lastPriorityEditRef.current = null
      lastUserIdEditRef.current = null
      lastAccountableUserIdEditRef.current = null
      lastSomedayEditRef.current = null
      lastEstimationEditRef.current = null
      lastStartDateEditRef.current = null
      lastDueDateEditRef.current = null
      setRecurrencePattern(null)
      return
    }

    const serverUpdatedOn = toTimestamp(task.updatedOn)

    // Handle name field with protection
    const nextName = task.name ?? ''
    const nameDecision = shouldAcceptServerValue(
      nextName,
      lastNameEditRef.current,
      serverUpdatedOn,
    )
    if (nameDecision.shouldAccept) {
      setNameValue(nextName)
      if (nameDecision.shouldClearRef) {
        lastNameEditRef.current = null
      }
    }

    // Handle status field with protection
    const nextStatus = task.statusId ?? null
    const statusDecision = shouldAcceptServerValue(
      nextStatus,
      lastStatusEditRef.current,
      serverUpdatedOn,
    )
    if (statusDecision.shouldAccept) {
      setStatusIdValue(nextStatus)
      if (statusDecision.shouldClearRef) {
        lastStatusEditRef.current = null
      }
    }

    // Handle priority field with protection
    const nextPriority = task.priority ?? 0
    const priorityDecision = shouldAcceptServerValue(
      nextPriority,
      lastPriorityEditRef.current,
      serverUpdatedOn,
    )
    if (priorityDecision.shouldAccept) {
      setPriorityValue(nextPriority)
      if (priorityDecision.shouldClearRef) {
        lastPriorityEditRef.current = null
      }
    }

    // Handle userId field with protection
    const nextUserId = task.userId ?? null
    const userIdDecision = shouldAcceptServerValue(
      nextUserId,
      lastUserIdEditRef.current,
      serverUpdatedOn,
    )
    if (userIdDecision.shouldAccept) {
      setUserIdValue(nextUserId)
      if (userIdDecision.shouldClearRef) {
        lastUserIdEditRef.current = null
      }
    }

    // Handle accountableUserId field with protection
    const nextAccountableUserId = task.accountableUserId ?? null
    const accountableUserIdDecision = shouldAcceptServerValue(
      nextAccountableUserId,
      lastAccountableUserIdEditRef.current,
      serverUpdatedOn,
    )
    if (accountableUserIdDecision.shouldAccept) {
      setAccountableUserIdValue(nextAccountableUserId)
      if (accountableUserIdDecision.shouldClearRef) {
        lastAccountableUserIdEditRef.current = null
      }
    }

    // Handle someday field with protection
    const nextSomeday = task.isSomeday ?? false
    const somedayDecision = shouldAcceptServerValue(
      nextSomeday,
      lastSomedayEditRef.current,
      serverUpdatedOn,
    )
    if (somedayDecision.shouldAccept) {
      setIsSomeday(nextSomeday)
      if (somedayDecision.shouldClearRef) {
        lastSomedayEditRef.current = null
      }
    }

    // Handle estimation field with protection
    const nextEstimation = task.estimation ?? null
    const estimationDecision = shouldAcceptServerValue(
      nextEstimation,
      lastEstimationEditRef.current,
      serverUpdatedOn,
    )
    if (estimationDecision.shouldAccept) {
      setEstimationValue(nextEstimation)
      if (estimationDecision.shouldClearRef) {
        lastEstimationEditRef.current = null
      }
    }

    // Handle workItemTypeId — no optimistic protection needed (infrequent change)
    setWorkItemTypeIdValue(task.workItemTypeId ?? null)

    // Handle due date field with protection
    const nextDueDate = task.dueDate
      ? normalizeToMinute(new Date(task.dueDate))
      : undefined
    const nextDueDateTimestamp = nextDueDate ? nextDueDate.getTime() : null
    const dueDateDecision = shouldAcceptServerValue(
      nextDueDateTimestamp,
      lastDueDateEditRef.current,
      serverUpdatedOn,
    )
    if (dueDateDecision.shouldAccept) {
      setDueDateValue(nextDueDate)
      if (dueDateDecision.shouldClearRef) {
        lastDueDateEditRef.current = null
      }
    }

    const nextStartDate = task.startDate
      ? normalizeToMinute(new Date(task.startDate))
      : undefined
    const nextStartDateTimestamp = nextStartDate ? nextStartDate.getTime() : null
    const startDateDecision = shouldAcceptServerValue(
      nextStartDateTimestamp,
      lastStartDateEditRef.current,
      serverUpdatedOn,
    )
    if (startDateDecision.shouldAccept) {
      setStartDateValue(nextStartDate)
      if (startDateDecision.shouldClearRef) {
        lastStartDateEditRef.current = null
      }
    }

  }, [
    task?.id,
    task?.name,
    task?.updatedOn,
    // task?.description removed - now in TaskContent
    task?.dueDate,
    task?.startDate,
    task?.isSomeday,
    task?.statusId,
    task?.priority,
    task?.userId,
    task?.accountableUserId,
    task?.estimation,
    task?.workItemTypeId,
  ])

  const handleNameBlur = useCallback(async () => {
    if (!task) {
      return
    }

    const trimmed = nameValue.trim()
    const current = (task.name ?? '').trim()

    if (trimmed.length === 0) {
      const fallback = current.length > 0 ? current : UNTITLED_TASK_NAME

      setNameValue(fallback)

      if (fallback !== current) {
        try {
          lastNameEditRef.current = { timestamp: Date.now(), value: fallback }
          await updateTask(task.id, {
            name: fallback,
          })
        } catch (error) {
          logger.logError(error as Error, 'TASK_UPDATE', {
            taskId: task.id,
            field: 'name',
            operation: 'fallback_to_untitled',
          })
          lastNameEditRef.current = null
        }
      }

      return
    }

    if (trimmed === current) {
      if (nameValue !== trimmed) {
        setNameValue(trimmed)
      }
      lastNameEditRef.current = null
      return
    }

    try {
      lastNameEditRef.current = { timestamp: Date.now(), value: trimmed }
      await updateTask(task.id, {
        name: trimmed,
      })
      setNameValue(trimmed)
    } catch (error) {
      logger.logError(error as Error, 'TASK_UPDATE', {
        taskId: task.id,
        field: 'name',
        value: trimmed,
      })
      lastNameEditRef.current = null
    }
  }, [nameValue, task?.id, task?.name, updateTask])

  const handleDueDateChange = useCallback(
    async (nextValue: Date | undefined) => {
      if (!task) {
        return
      }

      const normalized = nextValue ? normalizeToMinute(nextValue) : undefined
      const current = task.dueDate
        ? normalizeToMinute(new Date(task.dueDate))
        : undefined

      const currentTime = current ? current.getTime() : null
      const nextTime = normalized ? normalized.getTime() : null

      setDueDateValue(normalized)

      // Only save if actually changed
      if (currentTime !== nextTime) {
        try {
          lastDueDateEditRef.current = { timestamp: Date.now(), value: nextTime }
          await updateTask(task.id, {
            dueDate: normalized ? normalized.getTime() : null,
            dueDateType: normalized ? 'datetime' : null,
          })
          // Reminder clearing is now handled explicitly by Clear button
        } catch (error) {
          logger.logError(error as Error, 'TASK_UPDATE', {
            taskId: task.id,
            field: 'dueDate',
            value: nextTime,
          })
          setDueDateValue(current)
          lastDueDateEditRef.current = null
        }
      }
    },
    [task?.id, task?.dueDate, updateTask],
  )

  const handleStatusChange = useCallback(
    async (statusId: string) => {
      if (!task || !statusId || statusIdValue === statusId) {
        return
      }

      setStatusIdValue(statusId)

      try {
        lastStatusEditRef.current = {
          timestamp: Date.now(),
          value: statusId,
        }
        await updateTask(task.id, {
          statusId,
        })
      } catch (error) {
        logger.logError(error as Error, 'TASK_UPDATE', {
          taskId: task.id,
          field: 'statusId',
          value: statusId,
        })
        setStatusIdValue(task.statusId ?? null)
        lastStatusEditRef.current = null
      }
    },
    [task?.id, task?.statusId, statusIdValue, updateTask],
  )

  const handlePriorityChange = useCallback(
    async (priority: number) => {
      if (!task) {
        return
      }

      if (priorityValue === priority) {
        return
      }

      setPriorityValue(priority)

      try {
        lastPriorityEditRef.current = { timestamp: Date.now(), value: priority }
        await updateTask(task.id, {
          priority,
        })
      } catch (error) {
        logger.logError(error as Error, 'TASK_UPDATE', {
          taskId: task.id,
          field: 'priority',
          value: priority,
        })
        setPriorityValue(task.priority ?? 0)
        lastPriorityEditRef.current = null
      }
    },
    [task?.id, task?.priority, priorityValue, updateTask],
  )

  const handleUserIdChange = useCallback(
    async (userId: string | null) => {
      if (!task) {
        return
      }

      if (userIdValue === userId) {
        return
      }

      setUserIdValue(userId)

      try {
        lastUserIdEditRef.current = { timestamp: Date.now(), value: userId }
        await updateTask(task.id, {
          userId: userId ?? '',
        })
      } catch (error) {
        logger.logError(error as Error, 'TASK_UPDATE', {
          taskId: task.id,
          field: 'userId',
          value: userId,
        })
        setUserIdValue(task.userId ?? null)
        lastUserIdEditRef.current = null
      }
    },
    [task?.id, task?.userId, userIdValue, updateTask],
  )

  const handleAccountableUserIdChange = useCallback(
    async (userId: string | null) => {
      if (!task) {
        return
      }

      if (accountableUserIdValue === userId) {
        return
      }

      setAccountableUserIdValue(userId)

      try {
        lastAccountableUserIdEditRef.current = {
          timestamp: Date.now(),
          value: userId,
        }
        await updateTask(task.id, {
          accountableUserId: userId,
        })
      } catch (error) {
        logger.logError(error as Error, 'TASK_UPDATE', {
          taskId: task.id,
          field: 'accountableUserId',
          value: userId,
        })
        setAccountableUserIdValue(task.accountableUserId ?? null)
        lastAccountableUserIdEditRef.current = null
      }
    },
    [task?.id, task?.accountableUserId, accountableUserIdValue, updateTask],
  )


  const handleProjectChange = useCallback(
    async (projectId: string | null) => {
      if (!task) {
        return
      }

      try {
        // Clear the identifier/seq alongside the move: the server owns the
        // KEY-123 reassignment, and keeping the old pair would display the
        // previous project's key until the next pull lands.
        await updateTask(task.id, {
          projectId: projectId ?? '',
          identifier: null,
          seqNo: null,
        })
      } catch (error) {
        logger.logDebug('Project update failed, reverting optimistic update', {
          taskId: task.id,
          projectId,
        })
      }
    },
    [task?.id, updateTask],
  )


  const handleWorkItemTypeChange = useCallback(
    async (typeId: string) => {
      if (!task) return
      setWorkItemTypeIdValue(typeId)
      try {
        await updateTask(task.id, { workItemTypeId: typeId })
      } catch (error) {
        logger.logError(error as Error, 'TASK_UPDATE', {
          taskId: task.id,
          field: 'workItemTypeId',
          value: typeId,
        })
        setWorkItemTypeIdValue(task.workItemTypeId ?? null)
      }
    },
    [task?.id, task?.workItemTypeId, updateTask],
  )

  const handleEstimationChange = useCallback(
    async (estimation: number | null) => {
      if (!task) return

      setEstimationValue(estimation)

      try {
        lastEstimationEditRef.current = { timestamp: Date.now(), value: estimation }
        await updateTask(task.id, { estimation })
      } catch (error) {
        logger.logError(error as Error, 'TASK_UPDATE', {
          taskId: task.id,
          field: 'estimation',
          value: estimation,
        })
        setEstimationValue(task.estimation ?? null)
        lastEstimationEditRef.current = null
      }
    },
    [task?.id, task?.estimation, updateTask],
  )

  const handleArchive = useCallback(async () => {
    if (!task) return
    try {
      await archiveTask(task.id)
    } catch (error) {
      logger.logError(error as Error, 'TASK_UPDATE', {
        taskId: task.id,
        field: 'isArchived',
        operation: 'archive',
      })
    }
  }, [task?.id, archiveTask])

  const handleUnarchive = useCallback(async () => {
    if (!task) return
    try {
      await unarchiveTask(task.id)
    } catch (error) {
      logger.logError(error as Error, 'TASK_UPDATE', {
        taskId: task.id,
        field: 'isArchived',
        operation: 'unarchive',
      })
    }
  }, [task?.id, unarchiveTask])

  const handleIsAllDayChange = useCallback(
    async (nextValue: boolean) => {
      if (!task) {
        return
      }
      const current = task.isAllDay ?? false
      if (current === nextValue) {
        return
      }
      try {
        await updateTask(task.id, { isAllDay: nextValue })
      } catch (error) {
        logger.logError(error as Error, 'TASK_UPDATE', {
          taskId: task.id,
          field: 'isAllDay',
          value: nextValue,
        })
      }
    },
    [task?.id, task?.isAllDay, updateTask],
  )

  const handleStartDateChange = useCallback(
    async (nextValue: Date | undefined) => {
      if (!task) {
        return
      }

      const normalized = nextValue ? normalizeToMinute(nextValue) : undefined
      const current = task.startDate
        ? normalizeToMinute(new Date(task.startDate))
        : undefined

      const currentTime = current ? current.getTime() : null
      const nextTime = normalized ? normalized.getTime() : null

      setStartDateValue(normalized)

      if (currentTime !== nextTime) {
        try {
          lastStartDateEditRef.current = {
            timestamp: Date.now(),
            value: nextTime,
          }
          await updateTask(task.id, {
            startDate: normalized ? normalized.getTime() : null,
            startDateType: normalized ? 'datetime' : null,
          })
        } catch (error) {
          logger.logError(error as Error, 'TASK_UPDATE', {
            taskId: task.id,
            field: 'startDate',
            value: nextTime,
          })
          setStartDateValue(current)
          lastStartDateEditRef.current = null
        }
      }
    },
    [task?.id, task?.startDate, updateTask],
  )

  const handleSomedayChange = useCallback(
    async (nextValue: boolean) => {
      if (!task) {
        return
      }

      if (isSomeday === nextValue) {
        return
      }

      const previousValue = isSomeday
      const previousStartDate = startDateValue
      const previousDueDate = dueDateValue
      const previousReminderOffset = reminderOffsetMinutes

      // Optimistically update UI
      setIsSomeday(nextValue)

      if (nextValue) {
        // When marking as someday, clear all scheduling data
        setStartDateValue(undefined)
        setDueDateValue(undefined)
        setReminderOffsetMinutes(null)
        setRecurrencePattern(null)
      }

      try {
        lastSomedayEditRef.current = { timestamp: Date.now(), value: nextValue }

        if (nextValue) {
          // ATOMIC UPDATE: Clear all scheduling fields when marking as someday
          // Backend will enforce this anyway, but we do it here for instant UX
          await Promise.all([
            updateTask(task.id, {
              isSomeday: true,
              startDate: null,
              startDateType: null,
              dueDate: null,
              dueDateType: null,
            }),
            // Delete reminder if it exists
            primaryReminder ? deleteReminder(primaryReminder.id) : Promise.resolve(),
          ])
        } else {
          // Just update the flag when unmarking someday
          await updateTask(task.id, {
            isSomeday: false,
          })
        }
      } catch (error) {
        logger.logError(error as Error, 'TASK_UPDATE', {
          taskId: task.id,
          field: 'isSomeday',
          value: nextValue,
          operation: 'someday_toggle_with_scheduling_clear',
        })
        // Rollback optimistic updates on error
        setIsSomeday(previousValue)
        setStartDateValue(previousStartDate)
        setDueDateValue(previousDueDate)
        setReminderOffsetMinutes(previousReminderOffset)
        lastSomedayEditRef.current = null
      }
    },
    [
      task?.id,
      isSomeday,
      startDateValue,
      dueDateValue,
      reminderOffsetMinutes,
      primaryReminder,
      updateTask,
      deleteReminder,
    ],
  )

  const handleRecurrenceChange = useCallback(
    async (pattern: RecurrencePattern | null) => {
      if (!task || !userId) {
        return
      }

      setRecurrencePattern(pattern)

      // If there's an existing reminder, immediately save recurrence changes
      if (primaryReminder) {
        try {
          await updateReminder(primaryReminder.id, {
            recurrenceType: pattern?.recurrenceType ?? null,
            recurrenceInterval: pattern?.recurrenceInterval ?? null,
            recurrenceDaysOfWeek: pattern?.recurrenceDaysOfWeek ?? null,
            recurrenceDayOfMonth: pattern?.recurrenceDayOfMonth ?? null,
            recurrenceEndDate: pattern?.recurrenceEndDate
              ? new Date(pattern.recurrenceEndDate).getTime()
              : null,
            recurrenceMaxOccurrences: pattern?.recurrenceMaxOccurrences ?? null,
          })
        } catch (error) {
          logger.logError(error as Error, 'REMINDER_UPDATE', {
            reminderId: primaryReminder?.id,
            taskId: task.id,
            field: 'recurrencePattern',
          })
        }
      }
      // If no reminder exists yet, recurrence will be saved when reminder is created
    },
    [task?.id, userId, primaryReminder, updateReminder],
  )

  const handleReminderChange = useCallback(
    async (
      offsetMinutes: number | null,
      nextScheduledAt: number | null,
      pendingRecurrence?: RecurrencePattern | null,
    ) => {
      if (!task || !userId) {
        return
      }

      // When the picker saves reminder + recurrence together for a brand-new
      // reminder, React state from setRecurrencePattern hasn't flushed yet,
      // so we must use the pattern passed in directly rather than closure state.
      const effectiveRecurrence =
        pendingRecurrence !== undefined ? pendingRecurrence : recurrencePattern

      if (!workspaceId) {
        logger.logError(new Error('workspaceId is empty during reminder creation'), 'DATABASE', { taskId: task.id })
        return
      }

      // Check if offset actually changed
      const offsetChanged = offsetMinutes !== reminderOffsetMinutes
      // Check if nextScheduledAt changed (due date changed but offset stayed the same)
      const scheduleChanged = primaryReminder
        ? nextScheduledAt !== (primaryReminder as any).nextScheduledAt
        : false

      setReminderOffsetMinutes(offsetMinutes)

      try {
        if (offsetMinutes === null) {
          // Clear reminder (soft delete)
          if (primaryReminder) {
            await deleteReminder(primaryReminder.id)
          }
          setRecurrencePattern(null)
        } else if (primaryReminder && (offsetChanged || scheduleChanged)) {
          // Update if offset changed OR if nextScheduledAt changed (e.g. due date moved)
          // nextScheduledAt is already calculated by DateDurationPicker with fresh values
          await updateReminder(primaryReminder.id, {
            relativeOffsetMinutes: offsetMinutes,
            nextScheduledAt,
          })
        } else if (!primaryReminder) {
          // Create new reminder with offset AND recurrence
          // nextScheduledAt is already calculated by DateDurationPicker with fresh values
          await createReminder({
            id: crypto.randomUUID(),
            taskId: task.id,
            reminderType: 1, // 1 = Relative to due_date
            absoluteDate: null,
            relativeOffsetMinutes: offsetMinutes,
            notifyUserId: userId,
            notificationChannel: 'app',
            isEnabled: true,
            isSent: false,
            sentAt: null,
            recurrenceType: effectiveRecurrence?.recurrenceType ?? null,
            recurrenceInterval: effectiveRecurrence?.recurrenceInterval ?? null,
            recurrenceDaysOfWeek:
              effectiveRecurrence?.recurrenceDaysOfWeek ?? null,
            recurrenceDayOfMonth:
              effectiveRecurrence?.recurrenceDayOfMonth ?? null,
            recurrenceEndDate: effectiveRecurrence?.recurrenceEndDate
              ? new Date(effectiveRecurrence.recurrenceEndDate).getTime()
              : null,
            recurrenceMaxOccurrences:
              effectiveRecurrence?.recurrenceMaxOccurrences ?? null,
            occurrenceCount: 0,
            lastSentAt: null,
            nextScheduledAt,
          })
        }
      } catch (error) {
        logger.logError(
          error instanceof Error ? error : new Error(String(error)),
          'DATABASE',
          { operation: 'createReminder', taskId: task?.id },
        )
      }
    },
    [
      task?.id,
      userId,
      workspaceId,
      primaryReminder,
      recurrencePattern,
      reminderOffsetMinutes,
      createReminder,
      updateReminder,
      deleteReminder,
    ],
  )

  // Label handlers
  const handleLabelsChange = useCallback(
    async (newLabelIds: Array<string>) => {
      if (!task) {
        return
      }

      try {
        const currentLabelIds = taskLabels.map((taskLabel) => taskLabel.labelId)
        const toAdd = newLabelIds.filter((id) => !currentLabelIds.includes(id))
        const toRemove = currentLabelIds.filter(
          (id) => !newLabelIds.includes(id),
        )

        // Add new label assignments
        for (const labelId of toAdd) {
          // Find the label to get its name and color
          const label = availableLabels.find((l) => l.id === labelId)

          // Try to insert with retry logic (in case label is still syncing)
          let retries = 3
          let lastError = null

          while (retries > 0) {
            try {
              await createTaskLabel({
                taskId: task.id,
                labelId: labelId,
                labelName: label?.name || '',
                labelColor: label?.color || '#gray',
                labelGroupId: label?.labelGroupId ?? null,
              })
              break
            } catch (error: any) {
              lastError = error
              const isLabelNotFound = error?.response?.data?.code === 'MY-002'

              if (isLabelNotFound && retries > 1) {
                await new Promise((resolve) => setTimeout(resolve, 300))
                retries--
              } else {
                throw error
              }
            }
          }

          if (retries === 0 && lastError) {
            throw lastError
          }
        }

        // Remove label assignments
        for (const labelId of toRemove) {
          const existingTaskLabel = taskLabels.find(
            (taskLabel) => taskLabel.labelId === labelId,
          )
          if (existingTaskLabel) {
            await deleteTaskLabel(existingTaskLabel.id)
          }
        }
      } catch (error) {
        // Error updating labels
      }
    },
    [
      task?.id,
      workspaceId,
      taskLabels,
      availableLabels,
      createTaskLabel,
      deleteTaskLabel,
    ],
  )

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // Files pasted into the description editor (e.g. a screenshot) are uploaded
  // as task attachments — Tiptap has no inline image support here.
  const handlePasteFiles = useCallback(
    (files: Array<File>) => {
      files.forEach((file) => {
        void uploadFile(file)
      })
    },
    [uploadFile],
  )

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        try {
          await uploadFile(file)
        } catch (error) {
          // Error handling
        }
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [uploadFile],
  )

  const handleEntityLinkClick = useCallback(
    async (entityId: string, entityType: string) => {
      if (!workspaceSlug) return

      if (entityType === ENTITY_TYPES.WORK_ITEM) {
        onSubTaskClick?.(entityId)
        return
      }

      if (entityType === ENTITY_TYPES.PROJECT) {
        try {
          const db = getRxDB()
          const project = await db.projects.findOne(entityId).exec()
          if (project) {
            navigate({
              to: '/$workspaceSlug/projects/$projectKey',
              params: { workspaceSlug, projectKey: project.projectKey },
            })
          }
        } catch {
          // Project not found
        }
        return
      }

      if (entityType === ENTITY_TYPES.DOCUMENT) {
        try {
          const db = getRxDB()
          const page = await db.pages.findOne(entityId).exec()
          if (page) {
            const project = await db.projects.findOne(page.projectId).exec()
            if (project) {
              navigate({
                to: '/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
                params: {
                  workspaceSlug,
                  projectKey: project.projectKey,
                  pageSlug: page.slug,
                },
              })
            }
          }
        } catch {
          // Page or project not found
        }
      }
    },
    [workspaceSlug, navigate, onSubTaskClick],
  )

  // ---- Shared anatomy (design 16): header, blocks, chips, discussion ----

  const currentUser = userId
  // Design 30: the layout follows the width the detail actually has.
  const [bodyRef, bodyWidth] = useElementWidth<HTMLDivElement>()
  const [isTitleFocused, setIsTitleFocused] = useState(false)
  const withColumn = bodyWidth >= 760
  // Phones: the properties become two-up tiles under the title (design 36b).
  const isPhone = useIsMobile()
  const { checklists: taskChecklists } = useChecklists({ taskId: task.id })
  const { users: workspaceMembers } = useMembers()
  const { smartTime } = useDateTimeFormatters()
  const hasChecklists = taskChecklists.some((list) => !list.isDeleted)
  const [subTasksExpanded, setSubTasksExpanded] = useState(false)
  const defaultSubTaskStatusId = statuses.find((s) => s.progressType === 'todo')?.id ?? statuses[0]?.id ?? null
  const createSubTask = useCreateSubTask(task.id, workspaceId)
  const handleMakeSubTask = useCallback(
    async (content: string) => {
      if (!defaultSubTaskStatusId) return false
      try {
        return (await createSubTask({ name: content, statusId: defaultSubTaskStatusId })) !== null
      } catch {
        toast.error(t('subTask.makeFailed'))
        return false
      }
    },
    [createSubTask, defaultSubTaskStatusId, t],
  )

  const starToggle = <StarToggle entityType="task" entityId={task.id} />
  const optionsMenu = (
    <TaskOptionsMenu
      taskId={task.id}
      taskName={nameValue || 'Untitled task'}
      buttonClassName="p-1.5 hover:bg-secondary/80"
      isArchived={task.isArchived}
      onCoverImage={() => setIsCoverPickerOpen(true)}
      onArchive={handleArchive}
      onUnarchive={handleUnarchive}
    />
  )

  const renderHeader = () => {
    if (!chrome) return null
    return (
      <TaskDetailsHeader
        chrome={chrome}
        surface={layout === 'page' ? 'paper' : 'card'}
        projectLabel={
          taskProject ? (
            <>
              <ProjectIcon
                iconType={taskProject.iconType}
                icon={taskProject.icon}
                iconColor={taskProject.iconColor}
                size={14}
              />
              <span className="truncate">{taskProject.name}</span>
            </>
          ) : (
            <span>{t('detailHeader.inbox')}</span>
          )
        }
        identifier={taskIdentifier}
        taskUrl={workspaceSlug ? `${window.location.origin}/${workspaceSlug}/tasks/${task.id}` : null}
        star={starToggle}
        menu={optionsMenu}
      />
    )
  }

  const statusSlot = (
    <StatusComboBox
      statuses={statuses}
      selectedStatusId={statusIdValue}
      onStatusChange={(value) => {
        void handleStatusChange(value)
      }}
      fontSize="sm"
      buttonClassName="h-7 -ml-2 max-w-full justify-start gap-1.5 rounded-md border-0 bg-transparent px-2 text-[13.5px] font-normal text-foreground shadow-none hover:bg-secondary dark:bg-transparent dark:hover:bg-secondary"
    />
  )

  const labelsSlot = (
    <Popover open={isLabelPickerOpen} onOpenChange={setIsLabelPickerOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Manage labels"
          title={`Labels (${labelShortcutKeys.join(' + ')})`}
          className="-ml-2 inline-flex min-h-7 max-w-full items-center gap-1.5 rounded-md px-2 text-[13.5px] text-muted-foreground transition hover:bg-secondary"
        >
          {selectedLabels.length > 0 ? (
            <LabelList labels={selectedLabels} showRemove={false} />
          ) : (
            <>
              <Tag className="size-3.5" />
              {t('properties.addLabel')}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className={pickerPopoverClassName} sideOffset={PICKER_SIDE_OFFSET} align="start">
        <LabelSelectionPanel
          availableLabels={availableLabels}
          labelGroups={labelGroups}
          projects={projects}
          selectedLabelIds={selectedLabelIds}
          onSelectionChange={handleLabelsChange}
          projectId={task.projectId ?? null}
        />
      </PopoverContent>
    </Popover>
  )

  const renderProperties = (columns: 1 | 2, density: GridDensity) => (
    <div className="flex flex-col gap-2">
      <TaskMetadataSection
        layout="grid"
        gridColumns={columns}
        gridDensity={density}
        statusSlot={statusSlot}
        labelsSlot={labelsSlot}
        cycleSlot={
          workspaceId ? <TaskCycleField taskId={task.id} projectId={task.projectId ?? null} envId={workspaceId} /> : undefined
        }
        startDate={startDateValue}
        dueDate={dueDateValue}
        onStartDateChange={handleStartDateChange}
        onDueDateChange={handleDueDateChange}
        isAllDay={task.isAllDay ?? false}
        onIsAllDayChange={(value) => {
          void handleIsAllDayChange(value)
        }}
        reminderOffsetMinutes={reminderOffsetMinutes}
        onReminderChange={handleReminderChange}
        recurrencePattern={recurrencePattern}
        onRecurrenceChange={handleRecurrenceChange}
        taskCreatedOn={task.createdOn}
        priorityValue={priorityValue}
        onPriorityChange={(value) => {
          void handlePriorityChange(value)
        }}
        priorityPopoverOpen={isPriorityPopoverOpen}
        onPriorityPopoverOpenChange={setIsPriorityPopoverOpen}
        userIdValue={userIdValue}
        onUserChange={(value) => {
          void handleUserIdChange(value)
        }}
        accountableUserIdValue={accountableUserIdValue}
        onAccountableUserChange={(value) => {
          void handleAccountableUserIdChange(value)
        }}
        assigneePopoverOpen={isAssigneePopoverOpen}
        onAssigneePopoverOpenChange={setIsAssigneePopoverOpen}
        assigneeShortcutHint={assigneeShortcutKeys}
        projects={projects}
        selectedProjectId={task.projectId ?? null}
        onProjectChange={(projectId) => {
          void handleProjectChange(projectId)
        }}
        estimationValue={estimationValue}
        onEstimationChange={(value) => {
          void handleEstimationChange(value)
        }}
        estimationType={estimationType}
        projectPopoverOpen={isProjectPopoverOpen}
        onProjectPopoverOpenChange={setIsProjectPopoverOpen}
        projectShortcutHint={projectShortcutKeys}
        isSomeday={isSomeday}
        onSomedayChange={(value) => {
          void handleSomedayChange(value)
        }}
        workItemTypeId={workItemTypeIdValue}
        onWorkItemTypeChange={isWorkItemTypeEnabled ? (typeId) => {
          void handleWorkItemTypeChange(typeId)
        } : undefined}
      />

      {FEATURE_FLAGS.ENABLE_SUPER_AGENT && (
        <AiGateField
          layout="horizontal"
          value={task.aiGate ?? null}
          onChange={(value) => {
            void updateTask(task.id, { aiGate: value })
          }}
        />
      )}

      {FEATURE_FLAGS.ENABLE_GITHUB_INTEGRATION && (
        <LinkedPullRequestsSection
          taskId={task.id}
          taskName={task.name}
          taskIdentifier={task.identifier}
          projectId={task.projectId ?? null}
        />
      )}

      {!isPersonal && (
        <CustomFieldInline
          fields={customInlineFields}
          valueMap={customFieldValueMap}
          taskId={task.id}
          layout="horizontal"
        />
      )}
    </div>
  )

  const memberName = (userId: string | null | undefined) => {
    if (!userId) return null
    if (userId === currentUser) return t('detailHeader.you')
    const member = workspaceMembers.find((m) => m.userId === userId)
    return member?.name || member?.email || null
  }

  const renderMetaFooter = () => (
    <div className="space-y-0.5 text-[12px] text-muted-foreground">
      <p>
        {t('detailHeader.createdBy', {
          name: memberName(task.createdBy) ?? t('detailHeader.someone'),
          date: smartTime(new Date(task.createdOn)),
        })}
      </p>
      <p>{t('detailHeader.updated', { date: smartTime(new Date(task.updatedOn)) })}</p>
    </div>
  )

  // 26px beside a right column, 23px when stacked (design 30).
  const titleClass = withColumn
    ? 'text-[26px] font-semibold leading-[1.2] tracking-[-0.015em]'
    : 'text-[23px] font-semibold leading-[1.25] tracking-[-0.01em]'

  const renderTitleRow = (showActions: boolean) => (
    <div className="flex items-start gap-3">
      <div className="pt-1.5">
        <TaskStatusCheckbox
          currentStatusId={statusIdValue}
          onStatusChange={(value) => {
            void handleStatusChange(value)
          }}
          statusMode={statusMode}
          className="h-5 w-5"
        />
      </div>
      <div className="min-w-0 flex-1">
        <TextareaAutosize
          data-testid="task-name-input"
          value={nameValue}
          onChange={(event) => {
            if (event.target.value.length <= 500) {
              setNameValue(event.target.value)
            }
          }}
          onFocus={() => setIsTitleFocused(true)}
          onBlur={() => {
            setIsTitleFocused(false)
            void handleNameBlur()
          }}
          placeholder="Untitled task"
          minRows={1}
          // Never more than two lines until you edit it (design 30).
          maxRows={isTitleFocused ? undefined : 2}
          className={cn('w-full resize-none bg-transparent text-foreground focus:outline-none', titleClass)}
        />
      </div>
      {showActions && (
        <div className="flex items-center gap-1 pt-1">
          {starToggle}
          {optionsMenu}
        </div>
      )}
    </div>
  )

  const chipClass =
    'inline-flex h-7 items-center gap-1.5 rounded-md bg-secondary px-2.5 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground'

  /**
   * Description, then checklist, sub-tasks and files in that order once they
   * have content, then one row of quiet chips for reactions and whatever is
   * still empty (design 16a / 18a / 28). One flex-wrap row with `order` keeps a
   * single SubTaskSection mounted, so an in-progress add survives the switch
   * from chip to section.
   */
  const renderBlocks = () => (
    <div className="space-y-5">
      <section
        className={cn(
          'text-[14.5px] [&_.ProseMirror_h1]:text-[18px] [&_.ProseMirror_h2]:text-[16px] [&_.ProseMirror_h3]:text-[15px]',
          // Sections sit close under the description in every container (design 28 / 30).
          '[&_.ProseMirror]:min-h-12 [&_.ProseMirror]:py-1',
        )}
      >
        <TaskDescriptionEditor task={task} canEdit={true} onPasteFiles={handlePasteFiles} />
      </section>

      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-5">
        {hasChecklists && (
          <section className="order-1 min-w-0 basis-full">
            <ChecklistSection taskId={task.id} mode="full" onMakeSubTask={handleMakeSubTask} />
          </section>
        )}
        <div className={cn(subTasksExpanded ? 'order-2 min-w-0 basis-full overflow-x-hidden' : 'order-6')}>
          <SubTaskSection
            parentId={task.id}
            workspaceId={workspaceId}
            projectId={task.projectId}
            defaultStatusId={defaultSubTaskStatusId}
            statuses={statuses}
            mode="chip"
            onSubTaskClick={onSubTaskClick}
            statusMode={statusMode}
            onExpandedChange={setSubTasksExpanded}
          />
        </div>
        {shouldShowAttachmentsSection && (
          <section className="order-3 min-w-0 basis-full">
            <AttachmentsSection
              workspaceId={workspaceId ?? ''}
              entityType={ENTITY_TYPES.WORK_ITEM}
              entityId={task.id}
              externalUploadProgress={uploadProgress}
              variant="chips"
            />
          </section>
        )}
        {workspaceId && (
          <div className="order-4">
            <ReactionBar entityType="task" entityId={task.id} envId={workspaceId} />
          </div>
        )}
        {!hasChecklists && (
          <div className="order-5">
            <ChecklistSection taskId={task.id} mode="chip" />
          </div>
        )}
        {!shouldShowAttachmentsSection && (
          <button type="button" onClick={handleUploadClick} className={cn(chipClass, 'order-7')}>
            <Paperclip className="size-3.5" />
            {t('detailHeader.attach')}
          </button>
        )}
      </div>
    </div>
  )

  const discussionTabs: Array<DiscussionExtraTab> = [
    {
      id: 'relations',
      label: t('discussion.relations'),
      count: relations.length,
      content: (
        <RelationsSection taskId={task.id} workspaceId={workspaceId} onRelationClick={onSubTaskClick} />
      ),
    },
    {
      id: 'links',
      label: t('discussion.links'),
      count: entityLinks.length,
      content: (
        <div className="space-y-6">
          <ExplicitLinksSection
            entityId={task.id}
            entityType={ENTITY_TYPES.WORK_ITEM}
            entityName={task.name || ''}
            workspaceId={workspaceId}
            onLinkClick={handleEntityLinkClick}
          />
          <BacklinksSection entityId={task.id} onLinkClick={handleEntityLinkClick} />
        </div>
      ),
    },
    ...(isPersonal
      ? []
      : customFieldTabs.map(({ tab, ungrouped, groups }) => ({
          id: `cft:${tab.id}`,
          label: tab.name,
          content: (
            <CustomFieldTabContent
              ungrouped={ungrouped}
              groups={groups}
              valueMap={customFieldValueMap}
              taskId={task.id}
            />
          ),
        }))),
  ]

  const discussionStrip = workspaceId ? (
    <TaskDiscussionStrip
      taskId={task.id}
      envId={workspaceId}
      extraTabs={discussionTabs}
    />
  ) : null

  return (
    <>
    <div
      ref={taskDetailsRef}
      className={cn(
        'flex h-full min-h-0 max-h-full w-full flex-1 flex-col overflow-x-hidden',
        'overflow-y-auto',
        className,
      )}
    >
      {/* Hidden file input for inline attachment upload */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        className="hidden"
        accept={FILE_INPUT_ACCEPT}
        aria-label="Select file to upload as attachment"
      />

      {renderHeader()}

      {/* Design 30: one body for every container. Where the properties sit
          follows the width actually available: a sticky 264px right column
          from 760px, a grid under the title below that, one column under
          440px. Only the surface differs: full page is a white sheet inset
          in the paper, the modal is white, the side panel is a white card. */}
      <div className={cn('flex w-full flex-[1_0_auto] flex-col', layout === 'page' && 'bg-background px-3 py-3 phone:p-0')}>
        <div
          ref={bodyRef}
          className={cn(
            'flex flex-1 flex-col bg-card',
            // Same card as the side panel. clip, not hidden: hidden would make
            // this the scroll container and unstick the properties column.
            layout === 'page' && 'overflow-clip rounded-xl border phone:rounded-none phone:border-0',
          )}
        >
          <CoverImage
            displayUrl={coverDisplayUrl}
            meta={coverMeta}
            coverImageType={task.coverImageType}
            hasCover={hasCover}
            onChangeCover={() => setIsCoverPickerOpen(true)}
            onRemoveCover={() => void removeCoverImage()}
            onPositionChange={(y) => void updateCoverPosition(y)}
          />
          <div
            className={cn(
              'flex-1',
              withColumn
                ? 'grid grid-cols-[minmax(0,720px)_264px] grid-rows-[auto_1fr] justify-center gap-x-10 px-8'
                : 'flex flex-col gap-4 px-6',
            )}
          >
            <div className={cn('space-y-2', withColumn ? 'col-start-1 row-start-1 pt-8' : 'pt-4', hasCover && 'pt-2')}>
              {!hasCover && (
                <div className="group/cover">
                  <AddCoverButton onClick={() => setIsCoverPickerOpen(true)} />
                </div>
              )}
              {parentTask && (
                <ParentTaskIndicator
                  parentTask={parentTask}
                  statuses={statuses}
                  onParentTaskClick={onParentTaskClick}
                  onUnlink={() => void handleUnlinkParentTask()}
                />
              )}
              {(!chrome && taskIdentifier) || task.isArchived ? (
                <div className="flex flex-wrap items-center gap-2">
                  {!chrome && taskIdentifier && <TaskIdentifierPill identifier={taskIdentifier} />}
                  {task.isArchived && (
                    <Badge variant="secondary" className="gap-1">
                      <Archive />
                      Archived
                    </Badge>
                  )}
                </div>
              ) : null}
              {renderTitleRow(!chrome)}
            </div>

            {withColumn ? (
              // The aside stretches over both rows so its border runs the full
              // height; the inner wrapper is what sticks while scrolling. It
              // sticks below the h-11 header so the properties keep their pt-8.
              <aside className="col-start-2 row-span-2 row-start-1 border-l pl-6">
                <div className={cn('sticky pb-6 pt-8', chrome ? 'top-11' : 'top-0')}>
                  {renderProperties(1, 'column')}
                  <TaskProgressSummary taskId={task.id} workspaceId={workspaceId} statuses={statuses} />
                  <div className="mt-4 border-t pt-4">{renderMetaFooter()}</div>
                </div>
              </aside>
            ) : (
              <section className={cn('border-y py-3', isPhone && 'border-0 pt-1')}>
                {isPhone
                  ? renderProperties(2, 'tiles')
                  : renderProperties(bodyWidth > 0 && bodyWidth < 440 ? 1 : 2, 'stacked')}
                <TaskProgressSummary taskId={task.id} workspaceId={workspaceId} statuses={statuses} />
              </section>
            )}

            <div className={cn('flex min-w-0 flex-col', withColumn && 'col-start-1 row-start-2 pt-5')}>
              {renderBlocks()}
              {/* Inline under the content, never a separate strip (design 30). */}
              <div className="pb-3 pt-8">{discussionStrip}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CoverImagePicker
      open={isCoverPickerOpen}
      onOpenChange={setIsCoverPickerOpen}
      onSelectUnsplash={(photo) => void setCoverFromUnsplash(photo)}
      onUploadCustom={uploadCustomImage}
      isUploading={isCoverUploading}
      uploadProgress={coverUploadProgress}
    />
    </>
  )
}
